import React, { useState, useEffect } from 'react';
import { TextField, Grid, FormControlLabel, Checkbox } from '@mui/material';
import axios from '../../utils/axios.utils';

interface GtinPrefixData {
  prefix: string;
  company: string;
  description: string;
  active: boolean;
}

interface EditGtinPrefixFormProps {
  id: string;
  onSuccess: () => void;
  initialData?: Partial<GtinPrefixData>;
  showSnackbar?: (message: string, severity: 'success' | 'error') => void;
}

const EditGtinPrefixForm: React.FC<EditGtinPrefixFormProps> = ({ id, onSuccess, initialData, showSnackbar = () => {} }) => {
  const [formValues, setFormValues] = useState<GtinPrefixData>({
    prefix: '',
    company: '',
    description: '',
    active: true,
    ...initialData
  });

  useEffect(() => {
    if (initialData) {
      setFormValues(prev => ({
        ...prev,
        ...initialData
      }));
    }
  }, [initialData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = `api/admin/autotreat_mfr/gtin_prefixes/${id + '/' || ''}`;
      const method = id ? 'put' : 'post';
      
      await axios[method]<any, any>(url, formValues, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}`
        }
      });
      
      showSnackbar('GTIN prefix updated successfully!', 'success');
      onSuccess();
    } catch (error: any) {
      console.error('Error updating GTIN prefix:', error);
      const errorMessage = error?.response?.data?.message || 
        (error instanceof Error ? error.message : 'Failed to update GTIN prefix. Please try again.');
      showSnackbar(errorMessage, 'error');
    }
  };

  return (
    <form id="dialog-form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField 
            name="prefix" 
            label="Prefix" 
            fullWidth 
            variant="outlined" 
            required 
            value={formValues.prefix} 
            onChange={handleInputChange}
            inputProps={{
              maxLength: 15
            }}
            helperText={`${formValues.prefix?.length || 0}/15 characters`}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField 
            name="company" 
            label="Company" 
            fullWidth 
            variant="outlined" 
            value={formValues.company} 
            onChange={handleInputChange} 
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField 
            name="description" 
            label="Description" 
            fullWidth 
            variant="outlined" 
            value={formValues.description} 
            onChange={handleInputChange} 
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel 
            control={
              <Checkbox 
                name="active" 
                checked={formValues.active} 
                onChange={handleInputChange} 
              />
            } 
            label="Active" 
          />
        </Grid>
      </Grid>
    </form>
  );
};

export default EditGtinPrefixForm;
